// packages/shared/src/config/axios.js
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getEnvVar } from '../utils/getEnv';
import { getGlobalToken } from '../utils/tokenStore';

const SITE_SECRET_LOCAL_KEY = 'mysdb_site_secret';

/** -------------------------------------------------------
 * Session invalidation state (prevents double-logout)
 * ------------------------------------------------------ */
let sessionInvalidated = false;
let unauthorizedInFlight = null; // Promise to dedupe concurrent triggers

export function markSessionInvalidated() {
  sessionInvalidated = true;
}

export function clearSessionInvalidated() {
  sessionInvalidated = false;
  unauthorizedInFlight = null;
}

/** -------------------------------------------------------
 * Unauthorized handler (registered by platform)
 * ------------------------------------------------------ */
let onUnauthorized = null;

/**
 * Platform registers a callback invoked when:
 * - a stored JWT is detected as expired locally, OR
 * - the API returns 401/403
 *
 * Mobile should do: registerOnUnauthorized(hardLogout)
 */
export function registerOnUnauthorized(fn) {
  onUnauthorized = typeof fn === 'function' ? fn : null;
}

/** -------------------------------------------------------
 * Site secret (web sessionStorage / mobile env)
 * ------------------------------------------------------ */
let mobileSecret;
const isReactNative =
  typeof navigator !== 'undefined' && navigator.product === 'ReactNative';

if (isReactNative) {
  try {
    const req = typeof require !== 'undefined' ? require : null;
    if (req) {
      const Constants = req('expo-constants')?.default;
      mobileSecret = Constants?.expoConfig?.extra?.SITE_SECRET;
    }
  } catch {
    mobileSecret = undefined;
  }
}

// fallback to expo public env (dev)
if (!mobileSecret) {
  mobileSecret = process.env.EXPO_PUBLIC_SITE_SECRET;
}

/** -------------------------------------------------------
 * Base URL resolution
 * ------------------------------------------------------ */
function toApiBase(url) {
  if (!url || typeof url !== 'string') return undefined;
  let clean = url.trim().replace(/\/+$/, '');
  return clean.endsWith('/api') ? clean : `${clean}/api`;
}

export function resolveApiBaseUrl() {
  if (typeof window !== 'undefined' && window?.location) {
    const hostname = window.location.hostname;

    if (
      hostname === 'mysimpledbnotes.com' ||
      hostname === 'www.mysimpledbnotes.com'
    ) {
      return 'https://api.mysimpledbnotes.com/api';
    }
  }

  const v = getEnvVar('API_URL');
  console.log('resolveApiBaseURL API_URL', v);
  return toApiBase(v);
}

// Create axios instance (baseURL may be undefined initially; fixed in interceptor)
const instance = axios.create({
  baseURL: resolveApiBaseUrl(),
});

let baseURL = instance.defaults.baseURL;

export function setAxiosBaseURL(url) {
  const apiBase = toApiBase(url);
  if (!apiBase) return;
  baseURL = apiBase;
  instance.defaults.baseURL = apiBase;
}

export function getAxiosBaseURL() {
  return baseURL;
}

/** -------------------------------------------------------
 * Helpers
 * ------------------------------------------------------ */
function safePathname(full) {
  try {
    return new URL(full).pathname;
  } catch {
    return full;
  }
}

function isLoginPath(pathname) {
  return pathname === '/api/login' || pathname === '/api/auth/login';
}

function alreadyUnauthorizedHandled(config) {
  return !!config?.__unauthorizedHandled;
}

function markUnauthorizedHandled(config) {
  if (!config) return;
  config.__unauthorizedHandled = true;
}

/**
 * Trigger unauthorized exactly once (deduped).
 * Also marks config to avoid per-request loops.
 */
async function triggerUnauthorizedOnce(reason, config) {
  if (alreadyUnauthorizedHandled(config)) return;
  markUnauthorizedHandled(config);

  // If we already started invalidation, don't start again
  if (sessionInvalidated) return;

  // Mark invalidated immediately so concurrent requests won't race
  sessionInvalidated = true;

  if (!onUnauthorized) {
    console.warn(
      '[shared axios] Unauthorized but no onUnauthorized registered'
    );
    return;
  }

  if (!unauthorizedInFlight) {
    unauthorizedInFlight = (async () => {
      try {
        await onUnauthorized(reason);
      } catch (e) {
        console.warn('[shared axios] onUnauthorized failed', e?.message || e);
      }
    })();
  }

  await unauthorizedInFlight;
}

/** -------------------------------------------------------
 * ✅ Request interceptor: site secret + token injection + local expiry detection
 * ------------------------------------------------------ */
instance.interceptors.request.use(
  async (config) => {
    // Ensure baseURL at request time (fixes undefined/api/...)
    if (!config.baseURL || String(config.baseURL).includes('undefined')) {
      const resolved = resolveApiBaseUrl();
      if (resolved) {
        config.baseURL = resolved;
        instance.defaults.baseURL = resolved;
      } else {
        console.warn('API base URL not found. Did you set API_URL?');
      }
    }

    // Attach site secret
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const siteSecret = window.sessionStorage.getItem(SITE_SECRET_LOCAL_KEY);
        if (siteSecret) {
          config.headers = config.headers || {};
          config.headers['x-site-secret'] = siteSecret;
        }
      } else {
        if (mobileSecret) {
          config.headers = config.headers || {};
          config.headers['x-site-secret'] = mobileSecret;
        }
      }
    } catch {
      // ignore
    }

    // Detect login route (never attach Authorization and never invalidate)
    const full = `${config.baseURL || ''}${config.url || ''}`;
    const pathname = safePathname(full);
    const isLogin = isLoginPath(pathname);

    if (isLogin) {
      if (config.headers?.Authorization) delete config.headers.Authorization;
      return config;
    }

    // If caller already set Authorization, leave it alone
    if (config.headers?.Authorization) return config;

    // Get stored token
    let token = await getGlobalToken();

    // Local expiry detection
    if (token) {
      try {
        const { exp } = jwtDecode(token);
        const expired = typeof exp === 'number' && Date.now() >= exp * 1000;
        if (expired) {
          token = null;

          // IMPORTANT: trigger logout once, but do NOT keep doing it for every request
          await triggerUnauthorizedOnce('Session expired', config);

          // Don’t attach Authorization
          return config;
        }
      } catch {
        token = null;
      }
    }

    // Inject token if valid
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (err) => Promise.reject(err)
);

/**
 * Response interceptor:
 * invalidate the authenticated session on 401 only.
 *
 * 403 means the user is authenticated but is not authorized
 * for the requested resource/product, so it must not log them out.
 */
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const cfg = error?.config;

    // If already invalidated, ignore late failures from in-flight requests
    if (sessionInvalidated) {
      return Promise.reject(error);
    }

    if (status === 401) {
      // Avoid invalidating due to login endpoint itself
      try {
        const full = `${cfg?.baseURL || ''}${cfg?.url || ''}`;
        const pathname = safePathname(full);
        if (isLoginPath(pathname)) return Promise.reject(error);
      } catch {
        // ignore
      }

      console.warn('Unauthorized – JWT may be invalid or expired');
      await triggerUnauthorizedOnce('Unauthorized', cfg);
    }

    return Promise.reject(error);
  }
);

export default instance;
